<!-- src/views/TaskView.vue -->

<template>
  <div>
    <!-- =====================================================
         NO VENUE
    ====================================================== -->
    <div v-if="!resolvedVenueId" class="core-page">
      <div class="card-panel amber lighten-5 amber-text text-darken-4">
        No venue is currently selected.
      </div>
    </div>

    <!-- =====================================================
         LOADING
    ====================================================== -->
    <div v-else-if="loading" class="core-page">
      <div class="core-loading-state">Loading task board…</div>
    </div>

    <!-- =====================================================
         LOAD ERROR
    ====================================================== -->
    <div v-else-if="loadError" class="core-page">
      <div class="card-panel red lighten-5 red-text text-darken-3">
        {{ loadError }}
      </div>

      <div class="core-actions">
        <button type="button" class="btn blue-grey darken-2" @click="loadBoard">
          Retry
        </button>
      </div>
    </div>

    <!-- =====================================================
         BOARD
    ====================================================== -->
    <template v-else-if="board">
      <KanbanBoard
        :key="resolvedVenueId"
        :board="board"
        @change="handleBoardChange"
        @card-created="handleCardCreated"
        @card-updated="handleCardUpdated"
        @card-deleted="handleCardDeleted"
        @card-moved="handleCardMoved"
        @column-created="handleColumnCreated"
        @column-deleted="handleColumnDeleted"
      />

      <div v-if="saveError" class="task-save-error">
        {{ saveError }}
      </div>

      <div v-else-if="saving" class="task-save-status">Saving…</div>

      <div v-else-if="saved" class="task-save-status">Saved</div>
    </template>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";

import { useRoute } from "vue-router";

import KanbanBoard from "@/components/tasks/KanbanBoard.vue";

import { getTaskBoard, saveTaskBoard } from "@/scripts/sqlApiHelper/tasksApi";

import { useVenueContext } from "@/scripts/useScripts/useVenueContext";

/* =========================================================
   PROPS
========================================================= */

const props = defineProps({
  venueId: {
    type: String,
    default: null,
  },
});

/* =========================================================
   ROUTE / VENUE CONTEXT
========================================================= */

const route = useRoute();

const venueContext = useVenueContext();

/* =========================================================
   STATE
========================================================= */

const board = ref(null);

const loading = ref(false);

const saving = ref(false);

const saved = ref(false);

const loadError = ref("");

const saveError = ref("");

let saveTimer = null;

let savedTimer = null;

let refreshTimer = null;

let saveInFlight = false;

let localVersion = 0;

let savedLocalVersion = 0;

/* =========================================================
   STORED VENUE FALLBACK
========================================================= */

function getStoredVenueId() {
  try {
    return (
      localStorage.getItem("coredeck_venue") || localStorage.getItem("venue") || null
    );
  } catch {
    return null;
  }
}

/* =========================================================
   RESOLVED VENUE

   useVenueContext remains the main source of truth for
   the currently-selected CoreDeck venue.
========================================================= */

const resolvedVenueId = computed(() => {
  return (
    venueContext.venueId.value ||
    props.venueId ||
    route.params?.venueId ||
    getStoredVenueId() ||
    null
  );
});

/* =========================================================
   TOAST
========================================================= */

function showToast(message) {
  if (window.M?.toast) {
    window.M.toast({
      html: message,
    });

    return;
  }

  console.log("[TASK VIEW]", message);
}

/* =========================================================
   LOAD BOARD
========================================================= */

async function loadBoard() {
  const venueId = resolvedVenueId.value;

  clearTimeout(saveTimer);

  saveTimer = null;

  if (!venueId) {
    board.value = null;

    loadError.value = "";

    saveError.value = "";

    return;
  }

  loading.value = true;

  loadError.value = "";

  saveError.value = "";

  try {
    const loadedBoard = await getTaskBoard(venueId);

    /*
     * Do not apply the result if the venue changed while
     * this network request was running.
     */
    if (resolvedVenueId.value !== venueId) {
      return;
    }

    board.value = loadedBoard;

    localVersion = 0;

    savedLocalVersion = 0;
  } catch (error) {
    console.error("[TASK VIEW] Load failed:", error);

    if (resolvedVenueId.value !== venueId) {
      return;
    }

    board.value = null;

    loadError.value = error?.message || "Task board could not be loaded.";
  } finally {
    if (resolvedVenueId.value === venueId) {
      loading.value = false;
    }
  }
}

/* =========================================================
   BOARD CHANGE
========================================================= */

function handleBoardChange(nextBoard) {
  board.value = nextBoard;

  localVersion += 1;

  saveError.value = "";

  saved.value = false;

  queueSave();
}

/* =========================================================
   QUEUE SAVE
========================================================= */

function queueSave() {
  clearTimeout(saveTimer);

  saveTimer = setTimeout(() => {
    saveBoard();
  }, 700);
}

/* =========================================================
   SAVE BOARD
========================================================= */

async function saveBoard() {
  if (!board.value || !resolvedVenueId.value) {
    return;
  }

  if (saveInFlight) {
    return;
  }

  const venueId = resolvedVenueId.value;

  saveInFlight = true;

  saving.value = true;

  saved.value = false;

  saveError.value = "";

  const versionBeingSaved = localVersion;

  const snapshot =
    typeof structuredClone === "function"
      ? structuredClone(board.value)
      : JSON.parse(JSON.stringify(board.value));

  try {
    const result = await saveTaskBoard(venueId, snapshot);

    /*
     * Venue changed while save was running.
     */
    if (resolvedVenueId.value !== venueId) {
      return;
    }

    /*
     * Keep changes made while this request was running,
     * but advance the Firestore revision.
     */
    if (board.value) {
      board.value = {
        ...board.value,

        revision: result.revision,

        updatedAt: result.updatedAt,
      };
    }

    savedLocalVersion = versionBeingSaved;

    saved.value = true;

    clearTimeout(savedTimer);

    savedTimer = setTimeout(() => {
      saved.value = false;
    }, 1800);
  } catch (error) {
    console.error("[TASK VIEW] Save failed:", error);

    /*
     * Gmail or another browser changed the board after
     * this browser loaded it.
     *
     * taskBoard.js rejects the stale revision rather than
     * allowing us to overwrite the Gmail-created task.
     */
    if (error?.code === "TASK_BOARD_CONFLICT") {
      showToast("Tasks changed elsewhere. Loading the latest board.");

      await loadBoard();

      return;
    }

    saveError.value = error?.message || "Task board could not be saved.";
  } finally {
    saveInFlight = false;

    saving.value = false;
  }

  /*
   * Another local edit happened while save was in flight.
   */
  if (localVersion > savedLocalVersion) {
    queueSave();
  }
}

/* =========================================================
   SILENT REFRESH

   Checks every 30 seconds.

   This is what allows a task created from Gmail to appear
   on an already-open Tasks screen without refreshing the
   whole application.
========================================================= */

async function refreshBoardSilently() {
  const venueId = resolvedVenueId.value;

  if (
    !venueId ||
    !board.value ||
    loading.value ||
    saving.value ||
    saveInFlight ||
    localVersion > savedLocalVersion
  ) {
    return;
  }

  try {
    const latestBoard = await getTaskBoard(venueId);

    if (resolvedVenueId.value !== venueId) {
      return;
    }

    const currentRevision = Number(board.value?.revision || 0);

    const latestRevision = Number(latestBoard?.revision || 0);

    if (latestRevision > currentRevision) {
      board.value = latestBoard;

      localVersion = 0;

      savedLocalVersion = 0;

      showToast("Task board updated.");
    }
  } catch (error) {
    console.warn("[TASK VIEW] Silent refresh failed:", error);
  }
}

/* =========================================================
   CARD EVENTS
========================================================= */

function handleCardCreated(card) {
  console.log("[TASK VIEW] Card created:", card);
}

function handleCardUpdated(card) {
  console.log("[TASK VIEW] Card updated:", card);
}

function handleCardDeleted(payload) {
  console.log("[TASK VIEW] Card deleted:", payload);
}

function handleCardMoved(payload) {
  console.log("[TASK VIEW] Card moved:", payload);
}

/* =========================================================
   COLUMN EVENTS
========================================================= */

function handleColumnCreated(column) {
  console.log("[TASK VIEW] Column created:", column);
}

function handleColumnDeleted(payload) {
  console.log("[TASK VIEW] Column deleted:", payload);
}

/* =========================================================
   VENUE CHANGE
========================================================= */

watch(
  resolvedVenueId,

  () => {
    loadBoard();
  },

  {
    immediate: true,
  }
);

/* =========================================================
   MOUNT
========================================================= */

onMounted(() => {
  refreshTimer = setInterval(refreshBoardSilently, 30000);
});

/* =========================================================
   UNMOUNT
========================================================= */

onBeforeUnmount(() => {
  clearTimeout(saveTimer);

  clearTimeout(savedTimer);

  clearInterval(refreshTimer);
});
</script>

<style scoped>
/*
 * Generic styling remains in coredeck-ui.css.
 *
 * These are feature-specific fixed status indicators only.
 */

.task-save-status,
.task-save-error {
  position: fixed;

  right: 20px;

  bottom: 20px;

  z-index: 100;

  padding: 7px 12px;

  border-radius: var(--coredeck-radius-control);

  color: #ffffff;

  font-size: 0.8rem;

  font-weight: 600;

  box-shadow: var(--coredeck-shadow-small);
}

.task-save-status {
  background: var(--coredeck-neutral);
}

.task-save-error {
  background: var(--coredeck-danger);
}
</style>
